import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterMultiSelect from './FilterMultiSelect';
import {FilterOption} from '../../../common/types/FilterOption';
import peopleService from '../../services/peopleService';
import {handleError} from '../../../common/utils/errorHandling';
import './filters.scss';

interface PeopleFilterProps {
  people?: string[];
  onFilterChange: (people?: string[]) => void;
}

const PeopleFilter: React.FC<PeopleFilterProps> = ({
  people = [],
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempPeople, setTempPeople] = useState<string[]>(people);
  const [peopleOptions, setPeopleOptions] = useState<FilterOption[]>([]);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = people.length > 0;

  useEffect(() => {
    fetchPeople();
  }, []);

  const fetchPeople = async () => {
    handleError(async () => {
      const peopleData = await peopleService.getAllPeople();
      const options: FilterOption[] = peopleData.map(person => ({
        label: `${person.name} ${person.surname}`,
        value: person.id
      }));
      setPeopleOptions(options);
    }, 'Error fetching people');
  };

  const applyFilter = () => {
    const filteredPeople = tempPeople.length > 0 ? tempPeople : undefined;
    onFilterChange(filteredPeople);
  };

  const clearFilter = () => {
    setTempPeople([]);
    onFilterChange(undefined);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      setTempPeople(people);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    if (people.length === 0) {
      return t('gallery.filters.people');
    }
    return `${t('gallery.filters.people')} | ${people.length}`;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByPeople')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterMultiSelect
          value={tempPeople}
          options={peopleOptions}
          onChange={setTempPeople}
          onKeyDown={handleKeyPress}
          showFilter={true}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default PeopleFilter;
